import { Clients } from '@Balance/Clients/Domain/Model/Clients'
import { useClients } from '@Balance/Clients/Hooks/useClients'
import { AutoForm } from '@Core/Components/AutoForm/AutoForm'
import { Loader } from '@Core/Components/Loader/Loader'
import { DBInventoryDestination } from '@InventoryControl/Destinations/Data/DataSource/API/Entity/Destination'
import { useInventoryDestinations } from '@InventoryControl/Destinations/Hooks/useInventoryDestination/useInventoryDestination'
import { exitSchema } from '@InventoryControl/Exits/entities/Exits'
import { DBInventoryProduct } from '@InventoryControl/Products/Data/DataSource/API/Entity/Product'
import { useInventoryProducts } from '@InventoryControl/Products/Hooks/useProducts/useInventoryProducts'
import { formattedAutocomplete } from '@Utils/Formatters/formattedAutocomplete'
import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

export const ExitDetailsForm = ({
    state,
    editMode,
    handleChange,
    loading,
}: any) => {
    const { products, getProducts } = useInventoryProducts()
    const { destinations, getDestinations } = useInventoryDestinations()
    const { clients } = useClients()

    const {
        register,
        watch,
        formState: { errors },
        control,
        setValue,
    } = useForm({
        resolver: yupResolver(exitSchema),
        defaultValues: {
            ...state,
        },
    })

    const handleAutoCompleteChange =
        (name: string) =>
        (_: any, { value }: any) => {
            setValue(name, value)
        }

    const handleDateChange = (name: string) => (value: any) => {
        setValue(name, value)
    }

    const fillForm = useCallback(() => {
        Object.entries(state || {}).forEach(([key, value]) => {
            setValue(key, value)
            return true
        })
    }, [state])

    const inputs = useMemo(
        () => [
            {
                grid: 12,
                name: 'destination',
                label: 'Destinos',
                type: 'autocomplete',
                key: 'destination',
                labelKey: 'name',
                disabled: !editMode,
                value: state?.destination,
                register: register('destination'),
                onChange: handleAutoCompleteChange('destination'),
                results: formattedAutocomplete<DBInventoryDestination>(
                    destinations,
                    'name'
                ),
            },
            {
                grid: 12,
                name: 'client',
                label: 'Cliente',
                type: 'autocomplete',
                key: 'client',
                labelKey: 'corporateName',
                disabled: !editMode,
                value: state?.client,
                hide:
                    !state?.destination?.name?.includes('Cliente') &&
                    !state?.client,
                register: register('client'),
                onChange: handleAutoCompleteChange('client'),
                results: formattedAutocomplete<Clients>(
                    clients,
                    'corporateName'
                ),
            },
            {
                grid: 8,
                name: 'product',
                label: 'Produto',
                type: 'autocomplete',
                key: 'product',
                labelKey: 'name',
                value: state.product,
                register: register('product'),
                disabled: !editMode,
                onChange: handleAutoCompleteChange('product'),
                results: formattedAutocomplete<DBInventoryProduct>(
                    products,
                    'name'
                ),
            },
            {
                grid: 4,
                name: 'unitCost',
                register: register('unitCost'),
                label: 'Custo Unitário',
                control: control,
                value: state?.unitCost,
                type: 'input',
                key: 'unitCost',
                disabled: !editMode,
            },
            {
                grid: 4,
                name: 'quantity',
                register: register('quantity'),
                control: control,
                label: 'Quantidade',
                value: state?.quantity,
                type: 'input',
                key: 'quantity',
                disabled: !editMode,
            },
            {
                grid: 4,
                name: 'quantityReturned',
                register: register('quantityReturned'),
                control: control,
                label: 'Quantidade Devolvida',
                hide: !state?.quantityReturned,
                type: 'input',
                key: 'quantityReturned',
                value: state.quantityReturned,
                disabled: !editMode,
            },
            {
                grid: 4,
                name: 'soldAmount',
                register: register('soldAmount'),
                control: control,
                label: 'Quantidade Vendida',
                type: 'input',
                hide: !state?.soldAmount,
                value: state.soldAmount,
                key: 'soldAmount',
                disabled: !editMode,
            },
            {
                grid: 4,
                name: 'totalSalesValue',
                register: register('totalSalesValue'),
                control: control,
                label: 'Valor Total',
                type: 'input',
                key: 'totalSalesValue',
                value: state.totalSalesValue,
                disabled: !editMode,
            },
            {
                grid: 4,
                name: 'created_at',
                register: register('created_at'),
                label: 'Data da Saída',
                type: 'date',
                onChange: handleDateChange('created_at'),
                key: 'created_at',
                value: watch('created_at'),
                disabled: !editMode,
            },
            {
                grid: 12,
                name: 'observation',
                control: control,
                register: register('observation'),
                label: 'Observações',
                type: 'text',
                key: 'observation',
                value: state?.observation,
                disabled: !editMode,
                rows: 5,
            },
        ],
        [state]
    )

    useEffect(() => {
        const subscription = watch((value, { name }) => {
            return handleChange({
                // @ts-ignore
                target: { name, value: value[name] },
            })
        })
        return () => subscription.unsubscribe()
    }, [watch, state])

    useEffect(() => {
        getProducts()
        getDestinations()
        fillForm()
    }, [])

    if (!state || loading) return <Loader />

    return (
        <div className="mt-2">
            <AutoForm inputs={inputs} />
        </div>
    )
}
